<template>
  <div class="phone-field-row">
    <select
        :id="dialId"
        v-model="countryIdModel"
        class="form-control phone-dial-select"
        :disabled="disabled"
    >
      <option
          v-for="option in phoneDialOptions"
          :key="optionKey(option)"
          :value="option.id"
      >
        {{ option.text }}
      </option>
    </select>
    <b-form-input
        :id="numberId"
        v-model="numberModel"
        type="tel"
        inputmode="numeric"
        autocomplete="tel-national"
        class="phone-number-input"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        @keypress="blockNonNumericKey"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { BFormInput } from 'bootstrap-vue'
import {
  buildFullPhone,
  ensureDialInOptions,
  parseFullPhone,
  resolveDialOption,
  usePhoneCountries,
} from '@/libs/phoneNumber'

const props = defineProps({
  dial: {
    type: String,
    default: '',
  },
  number: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: 'phone',
  },
  placeholder: {
    type: String,
    default: 'Phone number',
  },
  maxlength: {
    type: [String, Number],
    default: 15,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  optionKeyPrefix: {
    type: String,
    default: '',
  },
  loadOnMount: {
    type: Boolean,
    default: true,
  },
  preferredCountryId: {
    type: [Number, String],
    default: null,
  },
})

const emit = defineEmits(['update:dial', 'update:number', 'change'])

const {
  phoneDialOptions,
  defaultDialCode,
  defaultDialOption,
  loadPhoneCountries,
} = usePhoneCountries()

const dialId = `${props.id}-code`
const numberId = props.id
const selectedCountryId = ref(null)

const optionKey = (option) => (
  props.optionKeyPrefix ? `${props.optionKeyPrefix}-${option.id}` : option.id
)

const preferredCountryId = () => (
  props.preferredCountryId != null && props.preferredCountryId !== ''
    ? props.preferredCountryId
    : window.companyCountryId
)

const sanitizeNationalNumber = (value) => {
  const maxLen = Number(props.maxlength) || 15
  return String(value ?? '').replace(/\D/g, '').slice(0, maxLen)
}

const blockNonNumericKey = (event) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return
  }

  const key = event.key
  if (key.length === 1 && !/\d/.test(key)) {
    event.preventDefault()
  }
}

const emitChange = (overrides = {}) => {
  const dial = overrides.dial ?? props.dial
  const number = overrides.number ?? props.number
  emit('change', {
    dial,
    number,
    fullPhone: buildFullPhone(dial, number),
  })
}

const syncSelectedCountryFromDial = (dialCode = props.dial) => {
  const options = phoneDialOptions.value
  if (!options.length) {
    return
  }

  let option = null
  if (!String(props.number || '').trim()) {
    option = defaultDialOption(preferredCountryId())
  } else if (dialCode) {
    option = resolveDialOption(options, dialCode, preferredCountryId())
  }

  if (!option) {
    option = defaultDialOption(preferredCountryId())
  }

  if (!option) {
    return
  }

  selectedCountryId.value = option.id
  if (String(option.value) !== String(props.dial || '')) {
    emit('update:dial', option.value)
    emitChange({ dial: option.value })
  }
}

const countryIdModel = computed({
  get: () => selectedCountryId.value,
  set: (countryId) => {
    selectedCountryId.value = countryId
    const option = phoneDialOptions.value.find(
      (item) => String(item.id) === String(countryId),
    )
    if (!option) {
      return
    }
    emit('update:dial', option.value)
    emitChange({ dial: option.value })
  },
})

const numberModel = computed({
  get: () => props.number,
  set: (value) => {
    const sanitized = sanitizeNationalNumber(value)
    emit('update:number', sanitized)
    emitChange({ number: sanitized })
  },
})

const setFromFullPhone = (phone) => {
  const parsed = parseFullPhone(
    phone,
    phoneDialOptions.value,
    defaultDialCode,
    preferredCountryId(),
  )
  selectedCountryId.value = parsed.countryId
  emit('update:dial', parsed.dialCode)
  emit('update:number', parsed.national)
  emitChange({ dial: parsed.dialCode, number: parsed.national })
}

watch(phoneDialOptions, () => {
  syncSelectedCountryFromDial()
})

watch(
  () => [props.dial, props.number, props.preferredCountryId],
  () => {
    // Keep label in sync when parent dial/number changes (e.g. edit load / reset).
    if (!phoneDialOptions.value.length) {
      return
    }

    const current = phoneDialOptions.value.find(
      (option) => String(option.id) === String(selectedCountryId.value),
    )
    if (current && String(current.value) === String(props.dial || '')) {
      return
    }

    syncSelectedCountryFromDial()
  },
)

onMounted(async () => {
  if (props.loadOnMount) {
    await loadPhoneCountries()
  }

  syncSelectedCountryFromDial()

  if (props.dial) {
    const dialRef = { value: props.dial }
    ensureDialInOptions(
      dialRef,
      phoneDialOptions.value,
      defaultDialCode,
      preferredCountryId(),
    )
    if (dialRef.value !== props.dial) {
      emit('update:dial', dialRef.value)
    }
  }
})

defineExpose({
  loadCountries: loadPhoneCountries,
  setFromFullPhone,
  buildFullPhone: () => buildFullPhone(props.dial, props.number),
})
</script>

<style scoped>
.phone-field-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.phone-dial-select {
  flex: 0 0 8.5rem;
  width: 8.5rem;
  max-width: 8.5rem;
  padding-left: 0.35rem;
  padding-right: 1.5rem;
  font-size: 0.875rem;
}

.phone-number-input {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
